import { Directive, Input, HostListener, ElementRef } from '@angular/core';
import { MAX_POSSIBLE_VALUE, REG_EX, keys } from 'sharedweb/helpers/shared-constants';
import { SharedUtils } from 'sharedweb/helpers/shared.utilities';
import { NgControl } from '@angular/forms';

@Directive({
    selector: '[appInputBound]'
})
export class InputBoundDirective {

    @Input() minValue: number;
    @Input() maxValue = MAX_POSSIBLE_VALUE;
    @Input() maxLength: number;
    @Input() precision: number;
    @Input() allowNegative: boolean = false;

    constructor(
        private el: ElementRef,
        private ngControl: NgControl
    ) { }

    @HostListener('keydown', ['$event']) onkeypress(event) {
        let curr_input = event.target.value;
        let specialEvents = keys.includes(event.keyCode);
        if (!specialEvents) {
            curr_input = this.validInput(curr_input, event.key, event.srcElement.selectionStart, event.srcElement.selectionEnd);
        }
        if (specialEvents || this._validateKey(curr_input)) {
            return;
        } else {
            event.preventDefault();
        }
    }

    /**
     * Applied textInput event because in case of keypress/keyup/keydown events on real
     * devices, event.key returns unIdentified, so regex can't be matched properly.
     * Need both events because textInput doesn't catch alphabets, so those are filtered
     * by keydown before textInput event is fired.
     * @param event 
     */
    @HostListener('textInput', ['$event']) onkeyup(event) {
        const curr_input = this.validInput(event.target.value, event.data, event.srcElement.selectionStart, event.srcElement.selectionEnd);
        if (SharedUtils.mobileAndTabletCheck() &&
            !this._validateKey(curr_input)) {
            event.preventDefault();
        }
    }


    private _validateKey(curr_input: string) {
        if (this.allowNegative) {
            if (curr_input === '-' && this.minValue < 0) {
                return true;
            }
            if (this.precision && !new RegExp(REG_EX.negative_float_values).test(curr_input)) {
                return false;
            }
            if (!this.precision && !new RegExp(REG_EX.negative_integer_values).test(curr_input)) {
                return false;
            }
            // Only check min/max if it's a valid number (not just '-', not just '-.')
            if (!isNaN(Number(curr_input)) && curr_input !== '-' && curr_input !== '-.') {
                if (Number(curr_input) < this.minValue || Number(curr_input) > this.maxValue) {
                    return false;
                }
            }
            return true;
        } else {
            // Only allow positive numbers
            if (parseInt(curr_input) < this.minValue ||
                parseInt(curr_input) > this.maxValue ||
                (this.precision && !new RegExp(REG_EX.contains_float_values).test(curr_input)) ||
                (!this.precision && !new RegExp(REG_EX.integer_only).test(curr_input))
            ) {
                return false;
            }
            return true;
        }
    }

    /**
     * created separate function, to assign exact input to the curr_input.
     * IF CASE: user entered value at last position then selectionStart will be 
     *          equal to curr_input length so appending the entered key to curr_input.
     * ELSE IF CASE: selectionStart and selectionEnd are same  
     *          IF CASE: if input is 20 and user entered a digit before 2 then, appending curr_input to key.
     *          ELSE CASE: if input is 20 and user entered digit 1 between 2 and 0
     *                     So, slicing the curr_input upto the selectionStart and appending key and then 
     *                     appending remaining value of curr_input.
     * ELSE CASE: if user selected the input or some part of the input and trying to replace the input then
     *            selectionStart and selectionEnd will be different. So, slicing curr_input upto the selectionStart 
     *            and appending key and then appending remaining value of curr_input from selectionEnd.                           
     */

    private validInput(curr_input, key, selectionStart, selectionEnd) {
        if (curr_input.length === selectionStart) {
            return curr_input + key;
        }
        else if (selectionStart === selectionEnd) {
            if (!selectionStart && !selectionEnd) {
                return key + curr_input;
            }
            else {
                return curr_input.slice(0, selectionStart) + key + curr_input.slice(selectionStart,);
            }
        }
        else {
            return curr_input.slice(0, selectionStart) + key + curr_input.slice(selectionEnd,);
        }
    }


    @HostListener('input', ['$event']) onInput(event) {
        const inputValue = event.target.value;
        let returnValue = inputValue;

        if (inputValue.length) {

            const values_list = inputValue.toString().split('.');

            // maxLength Check
            if (this.maxLength) {
                let value = values_list[0];
                if (value.length > this.maxLength) {
                    returnValue = value.substring(0, this.maxLength);
                }
            }

            // precision Check
            if (this.precision && values_list.length === 2 && values_list[1].length) {
                let value = values_list[1];
                if (value.length > this.precision) {
                    value = value.substring(0, this.precision);
                }
                returnValue = (returnValue.toString().split('.')[0] + '.' + value);
            }

        }
        this.el.nativeElement.value = returnValue;
        if (this.ngControl && this.ngControl.control) {
            this.ngControl.control.setValue(returnValue);
        }
    }
}
